import {
  createChartFrame,
  resolveTitle,
  clearSvg,
  getLayout,
  addLabel,
  createSvgEl,
  toNumber
} from './chart-utils.js';

const DEFAULT_MATRIX = [
  [2, 4, 6, 5, 4],
  [3, 5, 7, 6, 5],
  [4, 6, 8, 7, 6],
  [3, 5, 6, 5, 4]
];

export function render({ node }) {
  const { root, svg, legend, titleEl } = createChartFrame(node, 'Heatmap');

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Heatmap');
    legend.style.display = 'none';
    legend.innerHTML = '';

    clearSvg(svg);
    const layout = getLayout({ padTop: 18, padBottom: 24, padLeft: 28, padRight: 18 });

    const matrix = resolveMatrix(next);
    const rows = matrix.length;
    const cols = rows ? matrix[0].length : 0;
    if (!rows || !cols) return;

    const bounds = computeBounds(matrix, next);
    const cellW = layout.inner.w / cols;
    const cellH = layout.inner.h / rows;

    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const value = toNumber(matrix[r][c], 0);
        const norm = normalize(value, bounds.min, bounds.max);
        const opacity = 0.15 + norm * 0.85;
        const rect = createSvgEl('rect', {
          x: layout.inner.x + c * cellW,
          y: layout.inner.y + r * cellH,
          width: cellW,
          height: cellH,
          fill: 'var(--ew-ui-accent)',
          'fill-opacity': opacity,
          stroke: 'var(--ew-ui-panel-border)',
          'stroke-opacity': 0.35
        });
        rect.classList.add('ew-chart-cell');
        svg.appendChild(rect);
      }
    }

    const xLabels = next?.props?.xLabels || next?.data?.xLabels;
    const yLabels = next?.props?.yLabels || next?.data?.yLabels;

    if (Array.isArray(xLabels)) {
      xLabels.forEach((label, idx) => {
        if (idx >= cols) return;
        addLabel(svg, {
          x: layout.inner.x + (idx + 0.5) * cellW,
          y: layout.inner.y + layout.inner.h + 14,
          text: label,
          anchor: 'middle',
          className: 'ew-chart-label ew-chart-label-x'
        });
      });
    }
    if (Array.isArray(yLabels)) {
      yLabels.forEach((label, idx) => {
        if (idx >= rows) return;
        addLabel(svg, {
          x: 6,
          y: layout.inner.y + (idx + 0.5) * cellH + 3,
          text: label,
          anchor: 'start',
          className: 'ew-chart-label ew-chart-label-y'
        });
      });
    }
  };

  update(node);
  return { el: root, update };
}

function resolveMatrix(node) {
  const raw = node?.data?.matrix ?? node?.props?.matrix;
  if (Array.isArray(raw) && raw.length) {
    return raw.map((row) => (Array.isArray(row) ? row : []));
  }
  return DEFAULT_MATRIX;
}

function computeBounds(matrix, node) {
  let min = Infinity;
  let max = -Infinity;
  matrix.forEach((row) => {
    row.forEach((value) => {
      const n = toNumber(value, 0);
      if (n < min) min = n;
      if (n > max) max = n;
    });
  });
  if (!Number.isFinite(min)) min = 0;
  if (!Number.isFinite(max)) max = min + 1;

  const minOverride = toNumber(node?.props?.min ?? node?.data?.min, NaN);
  const maxOverride = toNumber(node?.props?.max ?? node?.data?.max, NaN);

  return {
    min: Number.isFinite(minOverride) ? minOverride : min,
    max: Number.isFinite(maxOverride) ? maxOverride : max
  };
}

function normalize(value, min, max) {
  if (!Number.isFinite(value)) return 0;
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return 1;
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}
